/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { formatCurrency, getErrorMessage } from '@sportninja/common/utils/utils'
import { Spin } from 'antd'
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import CreditCardInput from 'src/components/CreditCardInput'
import { weightSemiBold } from 'src/components/css'
import Icon from 'src/components/Icon'
import { EventCard } from 'src/components/RegistrationEventCard'
import { EventProgress } from 'src/components/RegistrationEventProgress'
import Button from 'src/components/ts-components/Button'
import Text from 'src/components/ts-components/Text'
import usePaymentProvider, {
  PAYMENT_PROVIDERS,
  PAYMENT_STATUS,
} from 'src/hooks/usePaymentProvider'
import useRegistrationPaymentWebSocket, {
  CONFIRMATION_STATE,
} from 'src/hooks/useRegistrationPaymentWebSocket'
import { registerError } from 'src/utils/sentry'
import LoggedUserBar from '../components/LoggedUserBar'
import PaymentMethodSelector from '../components/PaymentMethodSelector'
import { RegistrationContext } from '../context/RegistrationContext'
import {
  PageTitle,
  REGISTRATION_STEPS,
  alternateButtonStyle,
  headingButtonStyle,
  headingSixStyle,
  paragraphMediumBoldStyle,
  paragraphMediumStyle,
  paragraphSmallBoldStyle,
  paragraphSmallStyle,
} from './Main'

/**
 * RegistrationPayment - Handles the payment step of registration flow.
 * Renders payment info summary and supports Stripe, SportsPay, or in-person payment methods.
 * Uses WebSocket for real-time Stripe payment confirmation.
 * Side effects: API calls for payment processing, navigation to review step on success.
 */
const RegistrationPayment = () => {
  const {
    session,
    setStep,
    setSession,
    registration,
    deleteSession,
    currencyCode,
    couponInfo,
    division,
    role,
    teamOfficialType,
    isTeamRep,
    updateSessionConfirmSubmission,
    updateSessionConfirmSportsPaySubmission,
    countries,
    email,
    resetSession,
  } = useContext(RegistrationContext)

  const [
    submittingAlreadyInPersonPayment,
    setSubmittingAlreadyInPersonPayment,
  ] = useState(false)

  const ACTIVE_PAYMENT_PROVIDER =
    registration?.registration?.payment_provider_type?.id

  const waiverRequired = registration?.registration?.show_waiver

  // Local state for error handling
  const [localError, setLocalError] = useState(null)
  const [alreadyPaidInPerson, setAlreadyPaidInPerson] = useState(false)
  const [paymentMethod, setPaymentMethod] = useState(null)
  const [hasSubmittedPayment, setHasSubmittedPayment] = useState(false)
  const [isInSuccessDelay, setIsInSuccessDelay] = useState(false)

  // State for credit card input values (for SportsPay)
  const [creditCardValues, setCreditCardValues] = useState({
    cardNumber: '',
    expiryDate: '',
    cvc: '',
    country: '',
    cardType: '',
    errors: {},
  })

  // Ref to track if element has been mounted
  const elementMounted = useRef(false)

  const {
    total,
    tax,
    subtotal,
    client_secret,
    // This is used by sports pay to dinamically load the script
    host_name,
    root_schedule_name,
    connected_account_id,
  } = session

  // WebSocket hook for real-time payment confirmation (Stripe only)
  const {
    confirmationState,
    confirmedSession,
    errorMessage: wsErrorMessage,
    startWaitingForConfirmation,
    resetConfirmation,
    isWaiting: isWaitingForWsConfirmation,
  } = useRegistrationPaymentWebSocket(session?.id)

  /**
   * Whether the registration requires a single full payment (true) or uses
   * a deposit-first / installment flow (false).
   * Team reps use `division.single_payment`; players use `division.player_single_payment`.
   */
  const isSinglePayment = useMemo(() => {
    return isTeamRep
      ? !!division?.single_payment
      : !!division?.player_single_payment
  }, [isTeamRep, division?.single_payment, division?.player_single_payment])

  /**
   * The deposit amount relevant to the current registrant type.
   * Team reps use `division.deposit`; players use `division.player_deposit`.
   */
  const effectiveDeposit = useMemo(() => {
    return isTeamRep ? division?.deposit : division?.player_deposit
  }, [isTeamRep, division?.deposit, division?.player_deposit])

  const deposit = useMemo(() => {
    if (couponInfo?.id) {
      return formatCurrency(effectiveDeposit, currencyCode)
    }
    return formatCurrency(subtotal, currencyCode)
  }, [couponInfo?.id, subtotal, currencyCode, effectiveDeposit])

  /**
   * Handles the payment success flow with the following steps:
   * 1. For Stripe: Wait for WebSocket payment-confirmed event
   * 2. For SportsPay: Submit token and confirm via API
   * 3. For in-person: Confirm submission via API
   * @returns {Promise<boolean>} True if payment was successful, false otherwise
   */
  const handlePaymentSuccess = async ({ sportsPayToken }) => {
    try {
      // If this is an in-person payment, skip payment processing
      if (alreadyPaidInPerson) {
        try {
          setSubmittingAlreadyInPersonPayment(true)
          setIsInSuccessDelay(true)
          await updateSessionConfirmSubmission(alreadyPaidInPerson)
          setStep(REGISTRATION_STEPS.REGISTER_REVIEW)
          setSubmittingAlreadyInPersonPayment(false)
          setIsInSuccessDelay(false)
          return true
        } catch (submissionError) {
          setSubmittingAlreadyInPersonPayment(false)
          setPaymentStatus(PAYMENT_STATUS.FAILED)
          setIsInSuccessDelay(false)
          handlePaymentError(submissionError)
          return false
        }
      }

      // If this is SportsPay, use token-based submission
      if (ACTIVE_PAYMENT_PROVIDER === PAYMENT_PROVIDERS.SPORTS_PAY) {
        try {
          setIsInSuccessDelay(true)
          await updateSessionConfirmSportsPaySubmission(sportsPayToken)
          setStep(REGISTRATION_STEPS.REGISTER_REVIEW)
          setIsInSuccessDelay(false)
          return true
        } catch (submissionError) {
          setPaymentStatus(PAYMENT_STATUS.FAILED)
          setIsInSuccessDelay(false)
          handlePaymentError(submissionError)
          return false
        }
      }

      // For Stripe payments, wait for WebSocket confirmation
      // The backend will send payment-confirmed or confirmation-failed event
      console.log(
        '🔄 New_RegistrationPayment: Stripe payment successful, waiting for WebSocket confirmation'
      )
      setIsInSuccessDelay(true)
      startWaitingForConfirmation()
      return true
    } catch (sessionError) {
      // Handle session errors
      if (sessionError?.status === 410) {
        setLocalError('Session Has Expired. Please Try Again.')
        await new Promise((resolve) => setTimeout(resolve, 1500))
        deleteSession()
        window.location.reload()
      } else {
        const errorMessage = getErrorMessage(sessionError)
        setLocalError(errorMessage)
      }

      setHasSubmittedPayment(false)
      setIsInSuccessDelay(false)

      return false
    }
  }

  // Handle payment errors
  const handlePaymentError = useCallback(
    (err) => {
      console.error('Payment error:', err)

      // Get a user-friendly error message
      let errorMessage = getErrorMessage(err)

      // Handle specific error types
      if (err?.type === 'card_error') {
        // Card errors are user-facing errors that occur during payment processing
        errorMessage =
          err.message ||
          'Your card was declined. Please try another payment method.'
      } else if (err?.type === 'validation_error') {
        // Validation errors occur when the card details are invalid
        errorMessage =
          err.message || 'Please check your card details and try again.'
      } else if (
        err?.type === 'api_error' ||
        err?.type === 'api_connection_error'
      ) {
        // API errors are usually temporary issues with the payment service
        errorMessage =
          'We are experiencing issues with our payment service. Please try again later.'
      } else {
        // For any other errors, provide a generic message
        errorMessage =
          errorMessage ||
          'An error occurred while processing your payment. Please try again.'
      }

      // Set the error message to display to the user
      setLocalError(errorMessage)
      setHasSubmittedPayment(false)
      setIsInSuccessDelay(false)

      // Log the error to Sentry for tracking
      registerError(err, 'Payment processing error', {
        session,
        division,
        couponInfo,
        teamOfficialType,
        role,
      })
    },
    [session, division, couponInfo, teamOfficialType, role]
  )

  // Format credit card data for SportsPay
  const formatSportsPayCardData = useCallback(() => {
    if (!creditCardValues) {
      return null
    }

    // Extract month and year from expiry date (MM / YY format)
    const expiryParts = creditCardValues.expiryDate.split(' / ')
    const month = expiryParts[0]
    const year = expiryParts[1] ? `20${expiryParts[1]}` : ''

    return {
      CARDNUM: creditCardValues.cardNumber.replace(/\s+/g, ''),
      CARDEXPMM: month,
      CARDEXPYYYY: year,
      CARDCVV: creditCardValues.cvc,
    }
  }, [creditCardValues])

  // Initialize payment provider hook
  const {
    paymentStatus,
    setPaymentStatus,
    isLoading,
    error: paymentError,
    processPayment,
    resetPayment,
    mountStripeElement,
    validateSportsPayCard,
    isInitialized,
  } = usePaymentProvider({
    provider: ACTIVE_PAYMENT_PROVIDER,
    clientSecret: client_secret,
    sportsPayApiKey: client_secret,
    connectAccount: connected_account_id,
    sportsPayHost: host_name,
    onPaymentSuccess: handlePaymentSuccess,
    onPaymentError: handlePaymentError,
    sessionData: {
      session,
      division,
      couponInfo,
      teamOfficialType,
      role,
    },
  })

  // Handle WebSocket payment confirmation state changes (Stripe only)
  useEffect(() => {
    if (
      confirmationState === CONFIRMATION_STATE.CONFIRMED &&
      confirmedSession
    ) {
      console.log(
        '✅ New_RegistrationPayment: WebSocket confirmed, updating session and navigating'
      )
      setSession(confirmedSession)
      setStep(REGISTRATION_STEPS.REGISTER_REVIEW)
      setIsInSuccessDelay(false)
    } else if (
      confirmationState === CONFIRMATION_STATE.FAILED ||
      confirmationState === CONFIRMATION_STATE.TIMEOUT
    ) {
      console.log(
        '❌ New_RegistrationPayment: WebSocket confirmation failed:',
        wsErrorMessage
      )
      setPaymentStatus(PAYMENT_STATUS.FAILED)
      setLocalError(
        wsErrorMessage || 'Payment confirmation failed. Please try again.'
      )
      setHasSubmittedPayment(false)
      setIsInSuccessDelay(false)
    }
  }, [
    confirmationState,
    confirmedSession,
    wsErrorMessage,
    setSession,
    setStep,
    setPaymentStatus,
  ])

  // Set initial payment method based on deferred payment availability
  useEffect(() => {
    const isDeferredPaymentEnabled = isTeamRep
      ? division?.defer_payment
      : division?.player_defer_payment

    // Only set initial payment method if it's not already set
    if (paymentMethod === null && isDeferredPaymentEnabled === false) {
      setPaymentMethod('credit-card')
    }
  }, [
    isTeamRep,
    division?.defer_payment,
    division?.player_defer_payment,
    paymentMethod,
  ])

  // Sync alreadyPaidInPerson with paymentMethod
  useEffect(() => {
    setAlreadyPaidInPerson(paymentMethod === 'paid-in-person')
  }, [paymentMethod])

  // Set error if payment information is missing
  useEffect(() => {
    if (
      ACTIVE_PAYMENT_PROVIDER === PAYMENT_PROVIDERS.STRIPE &&
      (!client_secret || !connected_account_id)
    ) {
      setLocalError(
        !client_secret
          ? 'Payment information is missing. Please try again or contact support.'
          : 'Payment processing account is missing. Please try again or contact support.'
      )
    }
  }, [client_secret, connected_account_id])

  // Mount Stripe element when form is ready
  useEffect(() => {
    // Only mount the element if using Stripe
    if (ACTIVE_PAYMENT_PROVIDER !== PAYMENT_PROVIDERS.STRIPE) {
      return
    }

    // Only mount when credit card is selected
    if (paymentMethod !== 'credit-card') {
      return
    }

    // Only mount the element once
    if (elementMounted.current) {
      return
    }
    if (!isInitialized) {
      return
    }
    const paymentDiv = document.querySelector('#payment-element')
    if (paymentDiv && client_secret && connected_account_id) {
      mountStripeElement('payment-element')
      elementMounted.current = true
    }
  }, [
    client_secret,
    connected_account_id,
    mountStripeElement,
    isInitialized,
    paymentMethod,
    ACTIVE_PAYMENT_PROVIDER,
  ])

  // Validate SportsPay card data when it changes
  useEffect(() => {
    if (
      ACTIVE_PAYMENT_PROVIDER !== PAYMENT_PROVIDERS.SPORTS_PAY ||
      !validateSportsPayCard ||
      !isInitialized
    ) {
      return
    }

    // If there are errors, don't validate
    if (
      creditCardValues?.errors?.cardNumber ||
      creditCardValues?.errors?.expiryDate ||
      creditCardValues?.errors?.cvc
    ) {
      return
    }

    // Only validate if we have enough data
    if (
      creditCardValues.cardNumber &&
      creditCardValues.expiryDate &&
      creditCardValues.cvc &&
      creditCardValues.cvc.length > 3
    ) {
      const cardData = formatSportsPayCardData()
      if (cardData) {
        const validationResult = validateSportsPayCard(cardData)
        if (!validationResult.datavalid) {
          if (!validationResult.cardnum.isvalid) {
            if (validationResult.cardnum.message) {
              setLocalError(validationResult.cardnum.message)
            }
          }
          if (!validationResult.cardexp.isvalid) {
            if (validationResult.cardexp.message) {
              setLocalError(validationResult.cardexp.message)
            }
          }
          if (!validationResult.cardcvv.isvalid) {
            if (validationResult.cardcvv.message) {
              setLocalError(validationResult.cardcvv.message)
            }
          }
          return
        }
      }
    }
  }, [
    creditCardValues,
    validateSportsPayCard,
    isInitialized,
    formatSportsPayCardData,
    ACTIVE_PAYMENT_PROVIDER,
  ])

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault()
    // Clear any previous errors before processing payment
    setLocalError(null)
    setHasSubmittedPayment(true)

    // Validate that a payment method is selected
    if (!paymentMethod) {
      setLocalError('Please select a payment method')
      setHasSubmittedPayment(false)
      return
    }

    // If already paid in person, skip payment processing
    if (alreadyPaidInPerson) {
      await handlePaymentSuccess({})
      return
    }

    if (ACTIVE_PAYMENT_PROVIDER === PAYMENT_PROVIDERS.SPORTS_PAY) {
      // Check for credit card validation errors
      if (creditCardValues.errors?.expiryDate) {
        setLocalError(creditCardValues.errors.expiryDate)
        return
      }

      // Format card data for SportsPay
      const cardData = formatSportsPayCardData()
      if (!cardData) {
        setLocalError('Please enter valid card details')
        return
      }

      await processPayment(cardData)
    } else {
      // Stripe payment processing
      await processPayment()
    }
  }

  // Handle credit card input changes
  const handleCreditCardChange = useCallback((values) => {
    setCreditCardValues(values)
  }, [])

  // Reset payment provider and elements (used when unchecking paid in person checkbox)
  const handlePaymentReset = () => {
    resetPayment()
    resetConfirmation()
    // Reset the element mounted flag so it can be mounted again
    elementMounted.current = false
    // Clear any error messages
    setLocalError(null)

    // Force remount of Stripe element by adding a small delay
    if (ACTIVE_PAYMENT_PROVIDER === PAYMENT_PROVIDERS.STRIPE) {
      setTimeout(() => {
        const paymentDiv = document.querySelector('#payment-element')
        if (
          paymentDiv &&
          client_secret &&
          connected_account_id &&
          isInitialized
        ) {
          mountStripeElement('payment-element')
          elementMounted.current = true
        }
      }, 100)
    }
  }

  // Determine which error to display (local or from payment provider)
  const displayError = localError || paymentError

  // Combine all relevant loading/processing states to prevent session loss actions
  const isPaymentProcessing = useMemo(() => {
    return (
      hasSubmittedPayment &&
      (paymentStatus === PAYMENT_STATUS.PROCESSING ||
        isInSuccessDelay ||
        submittingAlreadyInPersonPayment ||
        isWaitingForWsConfirmation)
    )
  }, [
    hasSubmittedPayment,
    paymentStatus,
    isInSuccessDelay,
    submittingAlreadyInPersonPayment,
    isWaitingForWsConfirmation,
  ])

  // Warn user when attempting to close/refresh tab during payment processing
  useEffect(() => {
    if (!isPaymentProcessing) {
      return
    }

    const beforeUnloadHandler = (e) => {
      // Modern browsers ignore custom text; setting returnValue triggers the prompt.
      e.preventDefault()
      e.returnValue = ''
    }

    window.addEventListener('beforeunload', beforeUnloadHandler)
    return () => {
      window.removeEventListener('beforeunload', beforeUnloadHandler)
    }
  }, [isPaymentProcessing])

  // Create memoized UI fragments for each payment method
  const creditCardUI = useMemo(() => {
    if (paymentStatus === PAYMENT_STATUS.SUCCESS && !displayError) {
      return null
    }
    if (paymentStatus === PAYMENT_STATUS.FAILED || displayError) {
      return null
    }
    if (!ACTIVE_PAYMENT_PROVIDER) {
      return null
    }
    return (
      <>
        {ACTIVE_PAYMENT_PROVIDER === PAYMENT_PROVIDERS.SPORTS_PAY && (
          <CreditCardInput
            onChange={handleCreditCardChange}
            countries={countries}
            disabled={alreadyPaidInPerson}
          />
        )}
        {ACTIVE_PAYMENT_PROVIDER === PAYMENT_PROVIDERS.STRIPE && (
          <div id='payment-element' />
        )}
        <div
          css={css`
            display: flex;
            gap: 16px;
            width: 100%;
            margin-top: 16px;
          `}
        >
          <Button
            onClick={() => {
              if (waiverRequired && !isTeamRep) {
                setStep(REGISTRATION_STEPS.WAIVER)
              } else {
                setStep(REGISTRATION_STEPS.TEAM_REGISTER)
              }
            }}
            text='BACK'
            variant='secondary'
            disabled={isLoading}
          />
          <Button
            onClick={handleSubmit}
            text='PAY NOW'
            variant='primary'
            disabled={isLoading}
            isLoading={isLoading}
          />
        </div>
      </>
    )
  }, [
    paymentStatus,
    displayError,
    ACTIVE_PAYMENT_PROVIDER,
    handleCreditCardChange,
    countries,
    alreadyPaidInPerson,
    isLoading,
    waiverRequired,
    isTeamRep,
    setStep,
    handleSubmit,
  ])

  const paidInPersonUI = useMemo(() => {
    if (paymentStatus === PAYMENT_STATUS.SUCCESS && !displayError) {
      return null
    }
    if (paymentStatus === PAYMENT_STATUS.FAILED || displayError) {
      return null
    }

    return (
      <>
        <Button
          onClick={() => {
            if (waiverRequired && !isTeamRep) {
              setStep(REGISTRATION_STEPS.WAIVER)
            } else {
              setStep(REGISTRATION_STEPS.TEAM_REGISTER)
            }
          }}
          text='BACK'
          variant='secondary'
          disabled={isLoading || submittingAlreadyInPersonPayment}
        />
        <Button
          onClick={handleSubmit}
          text='SUBMIT REGISTRATION'
          variant='primary'
          disabled={isLoading || submittingAlreadyInPersonPayment}
          isLoading={isLoading || submittingAlreadyInPersonPayment}
        />
      </>
    )
  }, [
    paymentStatus,
    displayError,
    waiverRequired,
    isTeamRep,
    setStep,
    isLoading,
    submittingAlreadyInPersonPayment,
    handleSubmit,
  ])

  return (
    <>
      <div
        css={css`
          display: flex;
          flex-direction: column;
          flex: 1;
          justify-content: center;
          background-color: ${colors.NEUTRAL_800};
          width: 1080px;
          padding: 0 35px 35px 35px;
          border-radius: 16px 16px 0px 0px;
          @media (max-width: 768px) {
            min-width: 0;
            width: 100%;
          }
        `}
      >
        <LoggedUserBar
          email={email}
          disabled={isPaymentProcessing}
          onStartOver={() => {
            resetSession()
            setTimeout(() => {
              window.location.reload()
            }, 500)
          }}
        />
        <EventCard eventInfo={registration} />
        <EventProgress
          eventProgress={5}
          isWaiverRequired={waiverRequired}
          isTeamRep={isTeamRep}
        />
        <PageTitle
          title={isSinglePayment ? 'Pay Your Registration' : 'Pay Your Deposit'}
          subtitle={
            isSinglePayment
              ? 'Please make a payment to complete your registration for the competition.'
              : 'Please make a deposit payment to complete your registration for the competition.'
          }
          showAlert={true}
          showSuccess={false}
        />
        <div
          css={css`
            display: flex;
            justify-content: center;
            align-items: flex-start;
            gap: 16px;
            align-self: stretch;
            @media (max-width: 768px) {
              flex-direction: column;
              gap: 0px;
              align-items: center;
              max-height: unset;
            }
            ${ACTIVE_PAYMENT_PROVIDER === PAYMENT_PROVIDERS.SPORTS_PAY &&
            `
              max-height: unset;
            `}
          `}
        >
          {/* Payment Info - SportNinja Data */}
          <div
            css={css`
              display: flex;
              flex-direction: column;
              align-items: center;
              gap: 8px;
              flex: 1 0 0;
              @media (max-width: 768px) {
                margin-bottom: 16px;
                width: 100%;
              }
              background-color: ${colors.NEUTRAL_600};
              border-radius: 8px;
              border: 1px solid ${colors.NEUTRAL_400};
              padding: 16px;
            `}
          >
            <p
              css={css`
                ${paragraphMediumBoldStyle}
                color: ${colors.WHITE};
                align-self: stretch;
              `}
            >
              Payment Info
            </p>
            <div
              css={css`
                align-self: stretch;
                height: 0;
                border: 0.5px solid ${colors.NEUTRAL_400};
              `}
            />
            {effectiveDeposit && effectiveDeposit > 0 ? (
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  gap: 16px;
                  align-self: stretch;
                `}
              >
                <div
                  css={css`
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    align-items: flex-start;
                    flex: 1 0 0;
                  `}
                >
                  <p
                    css={css`
                      ${paragraphSmallStyle}
                      color: ${colors.WHITE};
                    `}
                  >
                    Deposit Fee
                  </p>
                  <p
                    css={css`
                      ${paragraphMediumBoldStyle}
                      color: ${colors.WHITE};
                    `}
                  >
                    {root_schedule_name}
                  </p>
                </div>
                <p
                  css={css`
                    ${paragraphMediumStyle}
                    color: ${colors.WHITE};
                  `}
                >
                  {deposit}
                </p>
              </div>
            ) : null}
            {/* Separator */}
            <div
              css={css`
                width: 100%;
                border-color: ${colors.VERY_DARK_GRAYISH_BLUE};
                border-bottom-width: 1px;
                border-bottom-style: solid;
              `}
            />
            <div
              css={css`
                display: flex;
                align-items: flex-start;
                gap: 16px;
                align-self: stretch;
              `}
            >
              <p
                css={css`
                  ${paragraphMediumStyle}
                  color: ${colors.WHITE};
                  flex: 1 0 0;
                `}
              >
                Subtotal
              </p>
              <p
                css={css`
                  ${paragraphMediumStyle}
                  color: ${colors.WHITE};
                `}
              >
                {formatCurrency(subtotal, currencyCode)}
              </p>
            </div>
            {couponInfo?.id && isSinglePayment && couponInfo?.is_installment ? (
              <div
                css={css`
                  display: flex;
                  align-items: flex-start;
                  gap: 16px;
                  align-self: stretch;
                `}
              >
                <p
                  css={css`
                    ${paragraphMediumStyle}
                    ${weightSemiBold}
                    color: ${colors.SUCCESS_100};
                    flex: 1 0 0;
                  `}
                >
                  Coupon Code: {couponInfo?.code}
                </p>
                <p
                  css={css`
                    ${paragraphMediumStyle}
                    ${weightSemiBold}
                    color: ${colors.SUCCESS_100};
                  `}
                >
                  -{formatCurrency(couponInfo?.discount_amount, currencyCode)}
                </p>
              </div>
            ) : null}
            {couponInfo?.id && !isSinglePayment && couponInfo?.is_deposit ? (
              <div
                css={css`
                  display: flex;
                  align-items: flex-start;
                  gap: 16px;
                  align-self: stretch;
                `}
              >
                <p
                  css={css`
                    ${paragraphMediumStyle}
                    ${weightSemiBold}
                    color: ${colors.SUCCESS_100};
                    flex: 1 0 0;
                  `}
                >
                  Coupon Code: {couponInfo?.code}
                </p>
                <p
                  css={css`
                    ${paragraphMediumStyle}
                    ${weightSemiBold}
                    color: ${colors.SUCCESS_100};
                  `}
                >
                  -
                  {formatCurrency(
                    couponInfo?.deposit_discount_amount,
                    currencyCode
                  )}
                </p>
              </div>
            ) : null}
            <div
              css={css`
                display: flex;
                align-items: flex-start;
                gap: 16px;
                align-self: stretch;
              `}
            >
              <p
                css={css`
                  ${paragraphMediumStyle}
                  color: ${colors.WHITE};
                  flex: 1 0 0;
                `}
              >
                Tax
              </p>
              <p
                css={css`
                  ${paragraphMediumStyle}
                  color: ${colors.WHITE};
                `}
              >
                {formatCurrency(tax, currencyCode)}
              </p>{' '}
            </div>

            {/* Separator */}
            <div
              css={css`
                align-self: stretch;
                height: 0;
                border: 0.5px solid ${colors.NEUTRAL_400};
              `}
            />
            <div
              css={css`
                display: flex;
                align-items: flex-start;
                gap: 22px;
                align-self: stretch;
              `}
            >
              <p
                css={css`
                  ${headingButtonStyle}
                  color: ${colors.WHITE};
                  flex: 1 0 0;
                `}
              >
                Total
              </p>
              <p
                css={css`
                  ${headingButtonStyle}
                  color: ${colors.WHITE};
                `}
              >
                {formatCurrency(total, currencyCode)}
              </p>
            </div>
            {isSinglePayment ? null : (
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  align-items: flex-start;
                  gap: 4px;
                  align-self: stretch;
                  /* display none if couponInfo is null */
                  display: ${couponInfo?.id && !couponInfo?.is_deposit
                    ? 'flex'
                    : 'none'};
                `}
              >
                <div
                  css={css`
                    display: flex;
                    align-items: flex-start;
                    gap: 16px;
                    align-self: stretch;
                  `}
                >
                  <p
                    css={css`
                      ${paragraphMediumStyle}
                      ${weightSemiBold}
                      color: ${colors.SUCCESS_100};
                      flex: 1 0 0;
                    `}
                  >
                    Coupon Code: {couponInfo?.code}
                  </p>
                  <p
                    css={css`
                      ${paragraphMediumStyle}
                      ${weightSemiBold}
                      color: ${colors.SUCCESS_100};
                    `}
                  >
                    -{formatCurrency(couponInfo?.discount_amount, currencyCode)}
                  </p>
                </div>
                <Text
                  variant='paragraphXSmall'
                  weight='regular'
                  color={colors.NEUTRAL_50}
                >
                  * Discount will be applied to the remaining balance.
                </Text>
              </div>
            )}
          </div>
          {/* Separator Between SportNinja and Stripe Stuff */}
          <div
            css={css`
              height: 100%;
              width: 1px;
              background-color: ${colors.VERY_DARK_GRAYISH_BLUE};
            `}
          />
          {/* Payment Provider UI */}
          <div
            css={css`
              width: 50%;
              display: flex;
              align-items: center;
              justify-content: flex-start;
              flex-direction: column;
              @media (max-width: 768px) {
                width: 100%;
                padding-top: 0px;
              }
            `}
          >
            <div
              css={css`
                width: 100%;
              `}
            >
              {paymentStatus === PAYMENT_STATUS.SUCCESS && !displayError ? (
                <div
                  css={css`
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    height: 250px;
                  `}
                >
                  <Icon name='check' color={colors.PRIMARY} fontSize={24} />
                  <p
                    css={css`
                      ${headingButtonStyle}
                      color: ${colors.WHITE};
                      margin-top: 32px;
                      text-align: center;
                    `}
                  >
                    {isWaitingForWsConfirmation
                      ? 'Payment Successful.\nConfirming your registration...'
                      : 'Payment Successful.\nYou will be redirected shortly.'}
                  </p>
                  <Spin
                    css={css`
                      margin-top: 32px;
                      color: ${colors.PRIMARY};
                    `}
                  />
                </div>
              ) : paymentStatus === PAYMENT_STATUS.FAILED || displayError ? (
                <div
                  css={css`
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    height: 300px;
                    width: 100%;
                    overflow: hidden;
                  `}
                >
                  <Icon name='times' color={colors.ERROR_LIGHT} fontSize={24} />
                  <p
                    css={css`
                      ${headingButtonStyle}
                      color: ${colors.WHITE};
                      margin-top: 32px;
                      text-align: center;
                      width: 100%;
                      overflow-wrap: break-word;
                      word-wrap: break-word;
                      word-break: break-word;
                    `}
                  >
                    Payment Failed
                  </p>
                  <span
                    css={css`
                      ${paragraphSmallBoldStyle}
                      color: ${colors.WHITE};
                      margin-top: 16px;
                      margin-bottom: 16px;
                      text-align: center;
                      max-width: 90%;
                      overflow-wrap: break-word;
                      word-wrap: break-word;
                      word-break: break-word;
                      overflow: hidden;
                      text-overflow: ellipsis;
                    `}
                  >
                    {displayError || 'Something went wrong'}
                  </span>
                  <div
                    css={css`
                      display: flex;
                      flex-direction: row;
                      justify-content: center;
                      align-items: center;
                      gap: 16px;
                      margin-top: 16px;
                      @media (max-width: 768px) {
                        flex-direction: column-reverse;
                        width: 100%;
                        gap: 8px;
                      }
                    `}
                  >
                    <button
                      type='button'
                      disabled={isLoading}
                      onClick={() => {
                        resetPayment()
                        resetConfirmation()
                        elementMounted.current = false
                        setLocalError(null)

                        if (
                          ACTIVE_PAYMENT_PROVIDER === PAYMENT_PROVIDERS.STRIPE
                        ) {
                          setTimeout(() => {
                            const paymentDiv =
                              document.querySelector('#payment-element')
                            if (
                              paymentDiv &&
                              client_secret &&
                              connected_account_id &&
                              isInitialized
                            ) {
                              mountStripeElement('payment-element')
                              elementMounted.current = true
                            }
                          }, 100)
                        }
                      }}
                      css={css`
                        ${alternateButtonStyle}
                        ${headingSixStyle}
                        width: 100%;
                        max-height: 40px;
                        cursor: ${isLoading ? 'not-allowed' : 'pointer'};
                      `}
                    >
                      BACK
                    </button>
                  </div>
                </div>
              ) : (
                <form id='payment-form' onSubmit={handleSubmit}>
                  <PaymentMethodSelector
                    isDeferredPaymentEnabled={
                      isTeamRep
                        ? division?.defer_payment
                        : division?.player_defer_payment
                    }
                    selectedMethod={paymentMethod}
                    onChange={(method) => {
                      if (
                        paymentMethod === 'paid-in-person' &&
                        method === 'credit-card'
                      ) {
                        handlePaymentReset()
                      }
                      // Reset element mounted flag when changing payment methods
                      if (paymentMethod !== method) {
                        elementMounted.current = false
                      }
                      setPaymentMethod(method)
                    }}
                    disabled={isLoading || submittingAlreadyInPersonPayment}
                    isPaymentProviderNotSet={!ACTIVE_PAYMENT_PROVIDER}
                  >
                    {paymentMethod === 'credit-card'
                      ? creditCardUI
                      : paymentMethod === 'paid-in-person'
                      ? paidInPersonUI
                      : null}
                  </PaymentMethodSelector>
                  {/* Show back button when no payment method is selected and defer payment is enabled */}
                  {paymentMethod === null &&
                    (isTeamRep
                      ? division?.defer_payment
                      : division?.player_defer_payment) && (
                      <div
                        css={css`
                          display: flex;
                          justify-content: flex-end;
                          width: 50%;
                          max-width: 200px;
                          margin-top: 16px;
                          position: absolute;
                          bottom: 32px;
                          right: 40px;
                          @media (max-width: 768px) {
                            width: 100%;
                            max-width: 100%;
                            position: relative;
                            bottom: 0;
                            right: 0;
                          }
                        `}
                      >
                        <Button
                          onClick={() => {
                            if (waiverRequired && !isTeamRep) {
                              setStep(REGISTRATION_STEPS.WAIVER)
                            } else {
                              setStep(REGISTRATION_STEPS.TEAM_REGISTER)
                            }
                          }}
                          text='BACK'
                          variant='secondary'
                          disabled={
                            isLoading || submittingAlreadyInPersonPayment
                          }
                          css={css`
                            width: 50%;
                          `}
                        />
                      </div>
                    )}
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default RegistrationPayment
